/**
 * M07 单样本失败定位表 · S2 Day 8b
 *
 * 默认仅显示 fail（intent_match=false 或 pb_match=false）· 可切到 all
 * 状态带图标（✓/✗/—）非纯颜色（X3 §12.1）
 */
import { CheckCircle2, MinusCircle, XCircle } from "lucide-react";
import { useMemo, useState } from "react";

import type { PerSampleResult } from "@/lib/api/eval";
import { intentLabel, playbookLabel } from "@/lib/product-labels";

interface Props {
	rows: PerSampleResult[];
}

type Filter = "fail_only" | "all";

function MatchCell({ match, label }: { match: boolean | null; label: string }) {
	if (match === null) {
		return (
			<span
				className="inline-flex items-center gap-1 text-xs text-text-muted"
				aria-label={`${label}未参与`}
			>
				<MinusCircle className="h-3.5 w-3.5" aria-hidden="true" />—
			</span>
		);
	}
	if (match) {
		return (
			<span
				className="inline-flex items-center gap-1 text-xs text-brand-success"
				aria-label={`${label}命中`}
			>
				<CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
				命中
			</span>
		);
	}
	return (
		<span
			className="inline-flex items-center gap-1 text-xs text-brand-danger"
			aria-label={`${label}未命中`}
		>
			<XCircle className="h-3.5 w-3.5" aria-hidden="true" />
			未中
		</span>
	);
}

function BadcaseCell({ row }: { row: PerSampleResult }) {
	if (!row.badcase_candidate) {
		return <span className="text-xs text-text-muted">—</span>;
	}
	return (
		<div className="min-w-36 space-y-1 text-xs leading-5">
			<span className="inline-flex rounded-sm border border-brand-warning/30 bg-brand-warning/10 px-1.5 py-0.5 font-medium text-brand-warning">
				badcase 候选
			</span>
			<div className="font-medium text-text-primary">{row.root_cause_hint ?? "待人工确认"}</div>
			<div className="text-text-muted">{row.responsible_asset ?? "责任资产待确认"}</div>
		</div>
	);
}

export function PerSampleTable({ rows }: Props) {
	const [filter, setFilter] = useState<Filter>("fail_only");
	const filtered = useMemo(() => {
		if (filter === "all") return rows;
		return rows.filter((r) => !r.intent_match || r.pb_match === false);
	}, [rows, filter]);

	const failCount = useMemo(
		() => rows.filter((r) => !r.intent_match || r.pb_match === false).length,
		[rows],
	);

	function rerunHref(row: PerSampleResult) {
		const params = new URLSearchParams({
			mock: "1",
			source: "eval",
			sample_id: row.sample_id,
			message: row.message,
			expected_intent: row.expected_intent,
			actual_intent: row.actual_intent,
		});
		if (row.expected_pb) params.set("expected_pb", row.expected_pb);
		if (row.actual_pb) params.set("actual_pb", row.actual_pb);
		if (row.root_cause_hint) params.set("root_cause_hint", row.root_cause_hint);
		if (row.responsible_asset) params.set("responsible_asset", row.responsible_asset);
		if (row.badcase_reason) params.set("badcase_reason", row.badcase_reason);
		return `/pipeline-testbench?${params.toString()}`;
	}

	return (
		<div className="space-y-2" data-testid="m07-per-sample">
			<div className="flex items-center justify-between text-sm">
				<div className="text-text-secondary">
					共 <span className="font-mono">{rows.length}</span> 条 ·
					<span className="ml-1 text-brand-danger">
						<span className="font-mono">{failCount}</span> 条 fail
					</span>
				</div>
				<div className="inline-flex overflow-hidden rounded-md border border-surface-border text-xs">
					<button
						type="button"
						data-testid="m07-filter-fail"
						className={`px-3 py-1 ${filter === "fail_only" ? "bg-brand-accent text-text-inverse" : "bg-surface-background text-text-secondary hover:bg-surface-subtle"}`}
						onClick={() => setFilter("fail_only")}
					>
						仅看 fail
					</button>
					<button
						type="button"
						data-testid="m07-filter-all"
						className={`px-3 py-1 ${filter === "all" ? "bg-brand-accent text-text-inverse" : "bg-surface-background text-text-secondary hover:bg-surface-subtle"}`}
						onClick={() => setFilter("all")}
					>
						全部
					</button>
				</div>
			</div>

			<div className="overflow-x-auto rounded-md border border-surface-border bg-surface-card">
				<table className="w-full border-collapse text-sm">
					<thead className="bg-surface-subtle text-text-secondary">
						<tr>
							<th className="px-2 py-2 text-left font-medium">ID</th>
							<th className="px-2 py-2 text-left font-medium">消息</th>
							<th className="px-2 py-2 text-left font-medium">期望意图</th>
							<th className="px-2 py-2 text-left font-medium">实际意图</th>
							<th className="px-2 py-2 text-left font-medium">意图</th>
							<th className="px-2 py-2 text-left font-medium">期望 PB</th>
							<th className="px-2 py-2 text-left font-medium">实际 PB</th>
							<th className="px-2 py-2 text-left font-medium">方案</th>
							<th className="px-2 py-2 text-left font-medium">归因</th>
							<th className="px-2 py-2 text-left font-medium">定位</th>
						</tr>
					</thead>
					<tbody>
						{filtered.length === 0 ? (
							<tr>
								<td
									colSpan={10}
									className="px-3 py-8 text-center text-sm text-text-muted"
									data-testid="m07-per-sample-empty"
								>
									{filter === "fail_only" ? "全部命中 · 无 fail 样本 ✓" : "无样本"}
								</td>
							</tr>
						) : (
							filtered.map((r) => (
								<tr
									key={r.sample_id}
									data-testid={`m07-row-${r.sample_id}`}
									className="border-t border-surface-border align-top"
								>
									<td className="px-2 py-2 font-mono text-xs">{r.sample_id}</td>
									<td className="px-2 py-2 text-text-primary">{r.message}</td>
									<td className="px-2 py-2 text-xs">{intentLabel(r.expected_intent)}</td>
									<td className="px-2 py-2 text-xs">{intentLabel(r.actual_intent)}</td>
									<td className="px-2 py-2">
										<MatchCell match={r.intent_match} label="意图" />
									</td>
									<td className="px-2 py-2 text-xs">{playbookLabel(r.expected_pb)}</td>
									<td className="px-2 py-2 text-xs">{playbookLabel(r.actual_pb)}</td>
									<td className="px-2 py-2">
										<MatchCell match={r.pb_match} label="方案" />
									</td>
									<td className="px-2 py-2">
										<BadcaseCell row={r} />
									</td>
									<td className="px-2 py-2">
										<a
											href={rerunHref(r)}
											className="text-xs font-medium text-brand-primary hover:underline"
										>
											复跑链路
										</a>
									</td>
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}
